import React, { useEffect, useState } from 'react';
import { MainLayout } from '../components/layouts/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { directionsAPI } from '../lib/firestore';
import { useToast } from '../components/ui/use-toast';
import type { Direction } from '../types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Download } from 'lucide-react';

export const DirectionsPage: React.FC = () => {
  const [directions, setDirections] = useState<Direction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newDirectionName, setNewDirectionName] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadDirections();
  }, []);

  const loadDirections = async () => {
    try {
      setLoading(true);
      const data = await directionsAPI.getAll();
      setDirections(data);
    } catch (error) {
      console.error('Error loading directions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load directions',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newDirectionName.trim()) {
      toast({
        title: 'Error',
        description: 'Direction name is required',
        className: 'bg-red-50 border-red-200',
      });
      return;
    }

    try {
      await directionsAPI.create({ name: newDirectionName.trim() });
      setIsAddDialogOpen(false);
      setNewDirectionName('');
      await loadDirections();
      toast({
        title: 'Success',
        description: 'Direction added successfully',
        className: 'bg-green-50 border-green-200',
      });
    } catch (error) {
      console.error('Error adding direction:', error);
      toast({
        title: 'Error',
        description: 'Failed to add direction',
        className: 'bg-red-50 border-red-200',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this direction?')) {
      try {
        await directionsAPI.delete(id);
        await loadDirections();
        toast({
          title: 'Success',
          description: 'Direction deleted successfully',
          className: 'bg-green-50 border-green-200',
        });
      } catch (error) {
        console.error('Error deleting direction:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete direction',
          className: 'bg-red-50 border-red-200',
        });
      }
    }
  };

  const handleInitFromApps = async () => {
    try {
      setIsInitializing(true);
      const functions = getFunctions();
      const initDirections = httpsCallable(functions, 'initDirectionsFromApps');
      const result = await initDirections({});

      await loadDirections();

      toast({
        title: 'Success',
        description: `Initialized ${(result.data as any).directionsCreated} directions from applications`,
        className: 'bg-green-50 border-green-200',
      });
    } catch (error: any) {
      console.error('Error initializing directions:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to initialize directions',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setIsInitializing(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Directions</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleInitFromApps}
              disabled={isInitializing}
            >
              <Download className="mr-2 h-4 w-4" />
              {isInitializing ? 'Initializing...' : 'Init from Apps'}
            </Button>
            <Button onClick={() => setIsAddDialogOpen(true)}>Add Direction</Button>
          </div>
        </div>

{loading ? (
          <div>Loading directions...</div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>All Directions ({directions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={directions}
                columns={[
                  {
                    key: 'name',
                    label: 'Name',
                    sortable: true,
                    render: (direction) => <span className="font-medium">{direction.name}</span>,
                  },
                  {
                    key: 'createdAt',
                    label: 'Created At',
                    sortable: true,
                    render: (direction) => new Date(direction.createdAt).toLocaleDateString(),
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    render: (direction) => (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(direction.id)}
                      >
                        Delete
                      </Button>
                    ),
                  },
                ]}
                searchKeys={['name']}
                emptyMessage="No directions found. Add your first direction."
                pageSize={25}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Direction</DialogTitle>
            <DialogDescription>
              Enter the name of the new direction.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={newDirectionName}
                onChange={(e) => setNewDirectionName(e.target.value)}
                placeholder="Direction name (e.g., DT, DRH, Marketing)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd}>Add Direction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
